import {useMemo} from "react";
import DashCard from "../../../../../../../../components/dash-card/dash-card.jsx";
import SentryEchart from "../../../../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../../../../utils/responsive-graph.js";
import ProjectUtils from "../../../../../../../../utils/project-utils.js";

export default function CitizensProfile({ data }) {
    const vw = useGraphViewportWidth();
    const childrenStatus = data?.protectedCitizens?.childrenStatus;
    const raceEthnicities = data?.protectedCitizens?.raceEthnicities;
    const educationLevels = data?.protectedCitizens?.educationLevels;
    const childrenQty = Array.isArray(childrenStatus)
        ? Number(childrenStatus.find(item => item.label === "childrenStatus")?.qty) || 0
        : 0;
    const chartOptions = useMemo(() => {
        const createOption = source => {
            const items = Array.isArray(source)
                ? source.map(item => ({
                    label: item.label ?? "",
                    value: Number(item.qty) || 0,
                }))
                : [];
            const labelFs = fluidPx(12, 21, vw);
            const valueFs = fluidPx(14, 20, vw);
            const max = Math.max(...items.map(item => item.value), 1);
            const labels = items.map(item => ProjectUtils.sentenceCase(item.label));
            const values = items.map(item => item.value);

            return {
                grid: {
                    top: labelFs,
                    left: "6%",
                    right: "7%",
                    bottom: 0,
                    outerBounds: {
                        top: labelFs,
                        left: "6%",
                        right: "7%",
                        bottom: 0,
                    },
                    outerBoundsContain: "axisLabel",
                },
                tooltip: {
                    show: true,
                    appendToBody: true,
                    confine: false,
                    extraCssText: "max-width: 320px; white-space: normal;",
                    formatter: params => `${params.name}: ${params.value}`,
                },
                xAxis: {
                    type: "value",
                    max,
                    show: false,
                },
                yAxis: {
                    type: "category",
                    data: labels,
                    inverse: true,
                    axisLabel: {show: false},
                    axisLine: {show: false},
                    axisTick: {show: false},
                },
                series: [
                    {
                        type: "bar",
                        barWidth: 13,
                        itemStyle: {
                            color: {
                                type: "linear",
                                x: 0,
                                y: 0,
                                x2: 1,
                                y2: 0,
                                colorStops: [
                                    {offset: 0, color: "#003878"},
                                    {offset: 0.28, color: "#0080ED"},
                                    {offset: 0.52, color: "#1CEAE6"},
                                    {offset: 0.72, color: "#3E98D9"},
                                    {offset: 1, color: "#003878"},
                                ],
                            },
                            borderRadius: 20,
                        },
                        label: {
                            show: true,
                            position: [0, -2],
                            align: "left",
                            verticalAlign: "bottom",
                            color: "#AAAAAA",
                            fontSize: labelFs,
                            fontWeight: 400,
                            formatter: params => labels[params.dataIndex],
                        },
                        showBackground: true,
                        backgroundStyle: {
                            color: "#3C3C4A",
                            borderRadius: 12,
                        },
                        data: values,
                    },
                    {
                        type: "bar",
                        barWidth: 13,
                        barGap: "-100%",
                        silent: true,
                        tooltip: {show: false},
                        itemStyle: {color: "transparent"},
                        label: {
                            show: true,
                            position: "right",
                            distance: 5,
                            color: "#FFFFFF",
                            fontSize: valueFs,
                            fontWeight: 600,
                            formatter: params => values[params.dataIndex],
                        },
                        data: values.map(() => max),
                    },
                ],
            };
        };

        return {
            race: createOption(raceEthnicities),
            education: createOption(educationLevels),
        };
    }, [educationLevels, raceEthnicities, vw]);

    return (
        <article className="col-span-4 box-border -mb-2 px-2 grid h-[calc(100%+8px)] min-h-0 min-w-0 w-full grid-cols-[minmax(0,1fr)] grid-rows-[58px_minmax(0,1fr)_minmax(0,1fr)]">
            <DashCard width={196} height={58} counterCard className="justify-self-center text-[20px] text-center">
                <span className="font-bold mr-2">{childrenQty}%</span> Possui filho
            </DashCard>
            <section className="flex h-full min-h-0 min-w-0 w-full overflow-hidden">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={chartOptions.race}
                    notMerge
                    title="Raça"
                    titleClass="translate-y-2"
                />
            </section>
            <section className="relative -top-1.5 flex h-[calc(100%+4px)] min-h-0 min-w-0 w-full overflow-hidden">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={chartOptions.education}
                    notMerge
                    title="Escolaridade"
                    titleClass="translate-y-2"
                />
            </section>
        </article>
    );
}
